"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BitcoinAddressConverter = void 0;
const bitcoinjs_lib_1 = require("bitcoinjs-lib");
const utils_1 = require("../utils");
const network_1 = require("./network");
/**
 * Creates the Bitcoin address from the public key. Supports SegWit (P2WPKH) and
 * Legacy (P2PKH) formats.
 * @param publicKey Compressed public key used to derive the Bitcoin address.
 * @param bitcoinNetwork Target Bitcoin network.
 * @param witness Flag to determine address format: true for SegWit (P2WPKH)
 *        and false for Legacy (P2PKH). Default is true.
 * @returns The derived Bitcoin address.
 */
function publicKeyToAddress(publicKey, bitcoinNetwork, witness = true) {
    const network = (0, network_1.toBitcoinJsLibNetwork)(bitcoinNetwork);
    if (witness) {
        // P2WPKH (SegWit)
        return bitcoinjs_lib_1.payments.p2wpkh({ pubkey: publicKey.toBuffer(), network }).address;
    }
    else {
        // P2PKH (Legacy)
        return bitcoinjs_lib_1.payments.p2pkh({ pubkey: publicKey.toBuffer(), network }).address;
    }
}
/**
 * Converts a public key hash into a P2PKH/P2WPKH address.
 * @param publicKeyHash Public key hash that will be encoded.
 * @param witness If true, a witness public key hash will be encoded and
 *        P2WPKH address will be returned. Returns P2PKH address otherwise
 * @param bitcoinNetwork Network the address should be encoded for.
 * @returns P2PKH or P2WPKH address encoded from the given public key hash.
 * @throws Throws an error if network is not supported.
 */
function publicKeyHashToAddress(publicKeyHash, witness, bitcoinNetwork) {
    const hash = publicKeyHash.toBuffer();
    const network = (0, network_1.toBitcoinJsLibNetwork)(bitcoinNetwork);
    return witness
        ? bitcoinjs_lib_1.payments.p2wpkh({ hash, network }).address
        : bitcoinjs_lib_1.payments.p2pkh({ hash, network }).address;
}
/**
 * Converts a P2PKH or P2WPKH address into a public key hash. Throws if the
 * provided address is not PKH-based.
 * @param address P2PKH or P2WPKH address that will be decoded.
 * @param bitcoinNetwork Network the address should be decoded for.
 * @returns Public key hash decoded from the address.
 */
function addressToPublicKeyHash(address, bitcoinNetwork) {
    const network = (0, network_1.toBitcoinJsLibNetwork)(bitcoinNetwork);
    try {
        // Try extracting hash from P2PKH address.
        return utils_1.Hex.from(bitcoinjs_lib_1.payments.p2pkh({ address: address, network }).hash);
    }
    catch (err) { }
    try {
        // Try extracting hash from P2WPKH address.
        return utils_1.Hex.from(bitcoinjs_lib_1.payments.p2wpkh({ address: address, network }).hash);
    }
    catch (err) { }
    // If neither of them succeeded, throw an error.
    throw new Error("Address must be P2PKH or P2WPKH valid for given network");
}
/**
 * Converts an address to the respective output script.
 * @param address BTC address.
 * @param bitcoinNetwork Bitcoin network corresponding to the address.
 * @returns The output script not prepended with length.
 */
function addressToOutputScript(address, bitcoinNetwork) {
    return utils_1.Hex.from(bitcoinjs_lib_1.address.toOutputScript(address, (0, network_1.toBitcoinJsLibNetwork)(bitcoinNetwork)));
}
/**
 * Converts an output script to the respective network-specific address.
 * @param script The output script not prepended with length.
 * @param bitcoinNetwork Bitcoin network the address should be produced for.
 * @returns The Bitcoin address.
 */
function outputScriptToAddress(script, bitcoinNetwork = network_1.BitcoinNetwork.Mainnet) {
    return bitcoinjs_lib_1.address.fromOutputScript(script.toBuffer(), (0, network_1.toBitcoinJsLibNetwork)(bitcoinNetwork));
}
/**
 * Utility functions allowing to perform Bitcoin address conversions.
 */
exports.BitcoinAddressConverter = {
    publicKeyToAddress,
    publicKeyHashToAddress,
    addressToPublicKeyHash,
    addressToOutputScript,
    outputScriptToAddress,
};
//# sourceMappingURL=address.js.map